import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { ExpenseWithDetails, ExpenseInput, ExpenseUpdate } from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';
import { pendingReceiptKeys } from '@/hooks/usePendingReceipts';
import { jobFileKeys } from '@/hooks/useJobFiles';

/**
 * The Files tab's view of a receipt, refreshed.
 *
 * Attaching one moves that file into the job's Expenses folder and marks it as
 * having an expense; detaching or deleting the expense releases it. All of that
 * happens server-side, so without this the Files tab keeps showing the photo in
 * the folder it was shot into until the page is reloaded. The folder list goes
 * with it — a move changes two file counts.
 */
function invalidateJobFiles(queryClient: ReturnType<typeof useQueryClient>, jobId: string): void {
  queryClient.invalidateQueries({ queryKey: jobFileKeys.lists(jobId) });
  queryClient.invalidateQueries({ queryKey: ['jobFolders'] });
}

interface ExpensesResponse {
  expenses: ExpenseWithDetails[];
}

interface ExpenseResponse {
  expense: ExpenseWithDetails;
}

export const expenseKeys = {
  all: ['expenses'] as const,
  lists: () => [...expenseKeys.all, 'list'] as const,
  list: (jobId: string) => [...expenseKeys.lists(), jobId] as const,
};

export function useExpenses(jobId: string | undefined) {
  return useQuery({
    queryKey: expenseKeys.list(jobId!),
    queryFn: async () => {
      const response = await apiClient.get<ExpensesResponse>('/expenses', {
        params: { jobId },
      });
      return response.expenses;
    },
    enabled: !!jobId,
  });
}

export function useCreateExpense(options?: { silent?: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: ExpenseInput) => {
      const response = await apiClient.post<ExpenseResponse>('/expenses', data);
      return response.expense;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.list(variables.jobId) });
      // Creating an expense consumes its linked pending receipt server-side
      // (POST /expenses deletes it). Invalidate the pending-receipt cache too,
      // or the "N receipts still need details" banner keeps rendering the
      // already-consumed receipt until an unrelated refetch clears it.
      queryClient.invalidateQueries({ queryKey: pendingReceiptKeys.list(variables.jobId) });
      invalidateJobFiles(queryClient, variables.jobId);
      if (!options?.silent) {
        toast({
          title: 'Expense added',
          description: 'The expense has been recorded.',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add expense.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateExpense(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { id: string } & ExpenseUpdate) => {
      const { id, ...update } = data;
      const response = await apiClient.put<ExpenseResponse>(`/expenses/${id}`, update);
      return response.expense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.list(jobId) });
      invalidateJobFiles(queryClient, jobId);
      toast({
        title: 'Expense updated',
        description: 'The expense has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update expense.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteExpense(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/expenses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.list(jobId) });
      // The file is untouched, but it is Unattached again, and the Files tab
      // says so — including whether it can now be moved or deleted.
      invalidateJobFiles(queryClient, jobId);
      toast({
        title: 'Expense deleted',
        description: 'The expense has been removed.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete expense.',
        variant: 'destructive',
      });
    },
  });
}
